import { HDate, HebrewCalendar } from '@hebcal/core';

/**
 * Compute tachanun rules for a given date.
 *
 * Uses HebrewCalendar.tachanun() as base, then applies mincha look-ahead
 * and computes lamnatzeach flag.
 *
 * @param {HDate} hdate - today's halachic date
 * @param {boolean} isIsrael
 * @param {object} calData - from calendarProvider (for isIsruChag)
 * @returns {object} tachanun flags
 */
export function computeTachanun(hdate, isIsrael, calData) {
  const base = HebrewCalendar.tachanun(hdate, isIsrael);

  // Mincha look-ahead: no tachanun at mincha if tomorrow has no tachanun at shacharit
  const tomorrow = new HDate(hdate.abs() + 1);
  const tomorrowTachanun = HebrewCalendar.tachanun(tomorrow, isIsrael);

  // Isru Chag override — HebrewCalendar might not handle all cases
  let shacharit = base.shacharit;
  if (calData.isIsruChag) {
    shacharit = false;
  }

  const mincha = base.mincha && tomorrowTachanun.shacharit;

  // Reason for skipping
  const reason = getReason(hdate, calData, shacharit);

  return {
    shacharit,
    mincha,
    allCongregations: base.allCongs !== undefined ? base.allCongs : true,
    lamnatzeach: shacharit, // Psalm 20 follows shacharit tachanun exactly
    reason,
  };
}

/**
 * Determine reason for skipping tachanun.
 */
function getReason(hdate, calData, shacharit) {
  if (shacharit) return null;

  const m = hdate.getMonth();
  const d = hdate.getDate();

  if (calData.isShabbat) return 'שבת';
  if (calData.isYomTov) return 'יום טוב';
  if (calData.isCholHamoed) return 'חול המועד';

  // All of Nisan (check before RC — 1 Nisan is both RC and Nisan)
  if (m === 1) return 'ניסן';

  if (calData.isRoshChodesh) return 'ר״ח';
  if (calData.isChanukah) return 'חנוכה';
  if (calData.isIsruChag) return 'איסרו חג';

  // 1-12 Sivan (Shavuot aftermath)
  if (m === 3 && d <= 12) return 'סיוון';

  // 9 Tishrei - 2 Cheshvan
  if (m === 7 && d >= 9) return d === 9 ? 'ערב יו״כ' : d === 10 ? 'יו״כ' : 'תשרי';
  if (m === 8 && d <= 2) return 'מרחשוון';

  // Specific dates
  if (m === 5 && d === 15) return 'ט״ו באב';
  if (m === 11 && d === 15) return 'ט״ו בשבט';
  if (m === 6 && d === 29) return 'ערב ר״ה';

  // Purim
  const adarMonth = HDate.isLeapYear(hdate.getFullYear()) ? 13 : 12;
  if (m === adarMonth && (d === 14 || d === 15)) return 'פורים';
  // Purim Katan
  if (HDate.isLeapYear(hdate.getFullYear()) && m === 12 && (d === 14 || d === 15)) return 'פורים קטן';

  // Lag B'Omer
  if (m === 2 && d === 18) return 'ל״ג בעומר';

  // Pesach Sheni
  if (m === 2 && d === 14) return 'פסח שני';

  // Tisha B'Av
  if (m === 5 && d === 9) return 'תשעה באב';

  // Modern holidays
  if (m === 2 && d === 5) return 'יום העצמאות';
  if (m === 2 && d === 28) return 'יום ירושלים';

  return null;
}
